<template>
  <div class="TripDisplayAction">
    <!-- Price -->
    <div class="TripDisplayAction-priceContainer">
      <p class="TripDisplayAction-priceLabel">
        <I18nT keypath="ui.booking.availability.results.price.from">
          <template #price>
            <span class="TripDisplayAction-price">
              {{ formattedPrice }}
            </span>
          </template>
        </I18nT>
      </p>

      <p v-if="multiPassengers && totalPrice" class="TripDisplayAction-priceLabel">
        {{ t('ui.booking.availability.results.price.total', { price: formattedTotalPrice }) }}
      </p>
    </div>

    <!-- Call to Action -->
    <div class="TripDisplayAction-ctaContainer">
      <ActionButton
        variant="secondary"
        class="TripDisplayAction-selectButton"
        :size="buttonSize"
        :loading
        :disabled
        @click="handleSelect"
      >
        {{ t('ui.booking.availability.results.select') }}
      </ActionButton>

      <p v-if="seatsLeft !== undefined" class="TripDisplayAction-seatsWarning">
        {{ t('ui.booking.availability.results.seatsLeft', seatsLeft) }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { usePriceFormatter, useResponsive } from '@cvo/ui/composables';
import { computed } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import ActionButton from '../../../atoms/ActionButton/ActionButton.vue';
import type { TripDisplayActionProps, TripDisplayActionEmits } from './TripDisplayAction.model';

const props = defineProps<TripDisplayActionProps>();
const emit = defineEmits<TripDisplayActionEmits>();

const { t } = useI18n();
const { isMobile } = useResponsive();
const { formattedPrice } = usePriceFormatter(() => props.price);
const { formattedPrice: formattedTotalPrice } = usePriceFormatter(() => props.totalPrice ?? null);

const buttonSize = computed(() => {
  return isMobile.value ? 'sm' : 'md';
});

// Handle select button click
function handleSelect() {
  emit('select');
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .TripDisplayAction {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-4;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;

    @include responsive.mobile {
      flex-direction: row;
      align-items: flex-start;
    }

    &-priceContainer,
    &-ctaContainer {
      @include responsive.mobile {
        flex: 1;
      }
    }

    &-priceContainer {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
    }

    &-price {
      color: vars.$organisms-tripdisplay-text-price-color;
      @include text.text(xl, $font-weight: vars.$font-weight-text-500);

      @include responsive.mobile {
        @include text.text(lg, $font-weight: vars.$font-weight-text-500);
      }
    }

    &-priceLabel {
      color: vars.$organisms-tripdisplay-text-secondary-color;
      @include text.text(xs);
    }

    &-ctaContainer {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
      align-items: center;
      justify-content: center;

      .TripDisplayAction-selectButton {
        @include responsive.mobile {
          width: 100%;
        }
      }
    }

    &-seatsWarning {
      color: vars.$organisms-tripdisplay-text-highlight-color;
      @include text.text(xs, $font-weight: vars.$font-weight-text-500);
      text-align: center;
    }
  }
}
</style>
